import type { CMSData, CmsPage } from "@/cms/models";
import { menuColour, menuRoles, roleChain } from "@/cms/menu-formatting";
import { resolveBackground } from "@/utils/background-layer";
import { record } from "@/utils/colour-values";
import { fillChangesInDark, ownColourCss } from "@/utils/colour-versions";
import type { HeaderSurfaces } from "@/utils/header-colours";
import { isLookSystem } from "@/utils/look-gate";
import {
  pageBackgroundOf,
  type ResolvedPageBackground,
} from "@/utils/page-background";
import { sectionLookAttributes } from "@/utils/section-look-attributes";

const NO_FILL = ["", "transparent", "none", "inherit", "currentcolor"];
const COLOUR_KEY = /colou?r$|farvetekst$/i;
const FILL_KEY = /(bg|background)colou?r$/i;
const ON_FILL = /(text|border)colou?r$/i;
/** The specifics a classic section draws a picture or video from (a URL, or `image.url`). */
const MEDIA_KEYS = ["bgImage", "imageUrl", "videoUrl", "image"];
const MEDIA_BACKGROUNDS = ["image", "video", "living"];
const isRecord = (value: unknown) => record(value) === value;
const filled = (value: unknown) =>
  typeof value === "string" && value.trim() !== "";
const seeThrough = (fill: unknown) =>
  fill == null ||
  (typeof fill === "string" && NO_FILL.includes(fill.trim().toLowerCase()));

/** The fill a box paints itself: its own background colour, or its surface's. */
const boxFill = (box: Record<string, unknown>) =>
  box.bgColor ?? box.backgroundColor ?? record(box.surface).backgroundColor;

interface Walk {
  theme: unknown;
  /** Keys left as they are, at any depth: element trees and colours another path routes. */
  skip: (key: string, depth: number) => boolean;
}

const boxChangesInDark = (
  theme: unknown,
  box: Record<string, unknown>,
  around: boolean,
) => {
  const fill = boxFill(box);
  return seeThrough(fill) ? around : fillChangesInDark(theme, fill);
};

/**
 * Routes the own colours of one box and the boxes inside it: each fill takes its dark version where `derived.darkOwn`
 * names it, and a text, border or rule colour where the fill under it changes in dark (`under`). That fill is the
 * box's own (`<name>BgColor` for `<name>TextColor`, else `bgColor`, `backgroundColor` or its surface's), else the one
 * around it.
 */
function routeBox(
  walk: Walk,
  box: Record<string, unknown>,
  around: boolean,
  depth: number,
  under = depth === 0 ? around : boxChangesInDark(walk.theme, box, around),
): Record<string, unknown> {
  let result: Record<string, unknown> | undefined;
  const set = (key: string, value: unknown) => {
    result ??= { ...box };
    result[key] = value;
  };
  for (const [key, value] of Object.entries(box)) {
    if (walk.skip(key, depth)) continue;
    if (isRecord(value) && depth < 3) {
      const inner = routeBox(
        walk,
        value as Record<string, unknown>,
        under,
        depth + 1,
      );
      if (inner !== value) set(key, inner);
      continue;
    }
    if (typeof value !== "string" || !COLOUR_KEY.test(key)) continue;
    const paired = ON_FILL.test(key)
      ? box[key.replace(ON_FILL, "BgColor")]
      : undefined;
    const routes = FILL_KEY.test(key)
      ? true
      : seeThrough(paired)
        ? under
        : fillChangesInDark(walk.theme, paired);
    if (!routes) continue;
    const painted = ownColourCss(walk.theme, value);
    if (painted !== value) set(key, painted);
  }
  return result ?? box;
}

const ELEMENT_TREES = new Set([
  "items",
  "rootComponent",
  "content",
  "cmsData",
  "app",
]);
/** Elements whose settings lie on the item itself rather than in its config, and whose `items` lie on their fill. */
const OWN_PROPS = new Set([
  "FlowComponent",
  "GridComponent",
  "SliderComponent",
]);

/** Whether every stop of a gradient the renderer draws as stored changes in dark: only a `var(--…)` reference does. */
const stopsChange = (steps: unknown[]) =>
  steps.every((step) => {
    const color = record(step).color;
    return typeof color === "string" && color.includes("var(--");
  });

/**
 * Whether the fill a Flow, Grid or Slider paints under its own text and its elements changes in dark, as
 * sectionFillChangesInDark decides for a section: never where it shows a picture or a video (`bgImage`, `video.url`,
 * or a picture, video or Levende `background`); else by the gradient FlowComponent lays over it (`gradient`, or the
 * `background`'s), whose stops it draws as stored; else by its own fill, else by the one around it. FlowComponent
 * paints no `background` colour.
 */
function containerChangesInDark(
  theme: unknown,
  box: Record<string, unknown>,
  around: boolean,
): boolean {
  if (
    filled(box.bgImage) ||
    filled(record(box.video).url) ||
    MEDIA_BACKGROUNDS.includes(
      resolveBackground(box.background, { living: true }).kind,
    )
  )
    return false;
  const steps = [
    record(box.gradient).steps,
    record(record(box.background).gradient).steps,
  ].find((value) => Array.isArray(value) && value.length > 0);
  return Array.isArray(steps)
    ? stopsChange(steps)
    : boxChangesInDark(theme, box, around);
}

/**
 * An element with its own colours' dark versions on the look system (Task 14.8): its own fills take their versions,
 * and its text, border and rule colours where the fill under them changes in dark: its own fill, else
 * `darkUnder`, whether the fill around it does. A Flow's, Grid's or Slider's own colours and elements lie on what it
 * paints (containerChangesInDark). A button is left to mergeCtaSettings. Routing a routed element again changes
 * nothing.
 */
export function elementWithDarkColours<T>(
  theme: unknown,
  item: T,
  darkUnder = false,
): T {
  if (!isLookSystem(theme) || !isRecord(item)) return item;
  const element = item as Record<string, unknown>;
  if (element.type === "CtaComponent") return item;
  const walk: Walk = { theme, skip: (key) => ELEMENT_TREES.has(key) };
  if (typeof element.type !== "string" || !OWN_PROPS.has(element.type)) {
    if (!isRecord(element.config)) return item;
    const config = routeBox(
      walk,
      element.config as Record<string, unknown>,
      darkUnder,
      1,
    );
    return (config === element.config ? item : { ...element, config }) as T;
  }
  const under = containerChangesInDark(theme, element, darkUnder);
  const routed = routeBox(walk, element, darkUnder, 1, under);
  const items = elementsWithDarkColours(theme, element.items, under);
  return (items === element.items ? routed : { ...routed, items }) as T;
}

function elementsWithDarkColours(
  theme: unknown,
  items: unknown,
  darkUnder: boolean,
): unknown {
  if (!Array.isArray(items)) return items;
  const routed = items.map((item) =>
    elementWithDarkColours(theme, item, darkUnder),
  );
  return routed.some((item, index) => item !== items[index]) ? routed : items;
}

/** Whether Sidens baggrund changes in dark: Design's page colour, or a colour or gradient that takes its version. */
export function pageFillChangesInDark(
  theme: unknown,
  background: ResolvedPageBackground | undefined,
): boolean {
  const fill = background?.fill;
  if (!fill || fill.kind === "none") return true;
  if (fill.kind === "image") return false;
  if (fill.kind === "gradient")
    return fill.gradient.steps.every((step) =>
      fillChangesInDark(theme, step.color),
    );
  return fill.color === undefined || fillChangesInDark(theme, fill.color);
}

/** Whether the section draws a picture, video or Levende, behind its content or beside it. */
function showsMedia(
  specifics: Record<string, unknown>,
  ui: Record<string, unknown>,
): boolean {
  return (
    filled(ui.bgImage) ||
    MEDIA_BACKGROUNDS.includes(
      resolveBackground(ui.background, { living: true }).kind,
    ) ||
    MEDIA_KEYS.some(
      (key) => filled(specifics[key]) || filled(record(specifics[key]).url),
    )
  );
}

/**
 * Whether a classic section's fill changes in dark, by what SectionLayout paints from its background
 * (`specifics.bgColor`, `ui.bgColor`, `specifics.farvebaggrund`): a palette colour changes, an own colour only where
 * sectionLookAttributes paints its derived fill; without one the page decides. A picture or video never does.
 */
function sectionFillChangesInDark(
  theme: unknown,
  section: { specifics?: unknown; ui?: unknown },
  page: boolean,
): boolean {
  const specifics = record(section.specifics);
  const ui = record(section.ui);
  const background = specifics.bgColor ?? ui.bgColor ?? specifics.farvebaggrund;
  return showsMedia(specifics, ui)
    ? false
    : typeof background !== "string" || seeThrough(background)
      ? page
      : background.toLowerCase().includes("var(--")
        ? fillChangesInDark(theme, background)
        : sectionLookAttributes(theme, section).background !== undefined;
}

const CONTENT_BOXES = ["content", "leftContent", "rightContent"];

/**
 * The elements of Fri opbygning's tree and of the content boxes on the fill under them. StackedContentComponent paints
 * `content.bgColor` in every box, inside the box's own `bgColor` that SplitContentSection paints.
 */
function sectionElementsInDark(
  theme: unknown,
  specifics: Record<string, unknown>,
  around: boolean,
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};
  const root = elementWithDarkColours(theme, specifics.rootComponent, around);
  if (root !== specifics.rootComponent) changes.rootComponent = root;
  const inner = record(specifics.content).bgColor;
  for (const name of CONTENT_BOXES) {
    const box = record(specifics[name]);
    const fill = [inner, box.bgColor].find((value) => !seeThrough(value));
    const under = fill === undefined ? around : fillChangesInDark(theme, fill);
    const items = elementsWithDarkColours(theme, box.items, under);
    if (items !== box.items) changes[name] = { ...box, items };
  }
  return changes;
}

/** A classic section's `items`, each a box on its own fill, else on the section's. */
function itemsInDark(walk: Walk, items: unknown, around: boolean): unknown {
  if (!Array.isArray(items)) return items;
  const routed = items.map((item) =>
    isRecord(item)
      ? routeBox(walk, item as Record<string, unknown>, around, 1)
      : item,
  );
  return routed.some((item, index) => item !== items[index]) ? routed : items;
}

/**
 * A classic section's settings with its own colours' dark versions on the look system (Task 14.8): its Tekstfarve
 * (`specifics.textColor`, `ui.textColor`, `specifics.farvetekst`), the colours in its specifics, its `items` and its
 * elements. The section's background is left alone and decides whether the text on it routes
 * (sectionFillChangesInDark). Boxes inside it with a fill of their own decide by that fill, a see-through one by the
 * fill around it. Where it routes `specifics.textColor`, `storedTextColor` keeps the stored one for the text a section
 * draws on a fill of its own that keeps its look (Sociale opslag's captions over the photos).
 */
export function classicSectionWithDarkColours<
  S extends { specifics?: unknown; ui?: unknown },
>(theme: unknown, section: S, page: boolean): S {
  if (!isLookSystem(theme)) return section;
  const specifics = record(section.specifics);
  const ui = record(section.ui);
  const around = sectionFillChangesInDark(theme, section, page);
  const walk: Walk = {
    theme,
    skip: (key, depth) =>
      key === "rootComponent" ||
      key === "items" ||
      (depth === 0 && ["bgColor", "farvebaggrund"].includes(key)),
  };
  const routed = isRecord(section.specifics)
    ? routeBox(walk, specifics, around, 0)
    : section.specifics;
  const items = itemsInDark(walk, specifics.items, around);
  const elements = {
    ...sectionElementsInDark(theme, record(routed), around),
    ...(items === specifics.items ? {} : { items }),
  };
  const withElements = Object.keys(elements).length
    ? { ...record(routed), ...elements }
    : routed;
  const textColor =
    around && typeof ui.textColor === "string"
      ? ownColourCss(theme, ui.textColor)
      : ui.textColor;
  if (withElements === section.specifics && textColor === ui.textColor)
    return section;
  const storedTextColor = specifics.textColor;
  return {
    ...section,
    specifics: withElements,
    ...(textColor === ui.textColor ? {} : { ui: { ...ui, textColor } }),
    ...(typeof storedTextColor === "string" &&
    record(withElements).textColor !== storedTextColor
      ? { storedTextColor }
      : {}),
  };
}

/** Whether Sidens baggrund of this page changes in dark, for the classic sections drawn on it. */
export function pageChangesInDark(
  cmsData: Pick<CMSData, "layout"> & { theme?: unknown },
  page: Pick<CmsPage, "layout"> | undefined,
): boolean {
  return pageFillChangesInDark(cmsData.theme, pageBackgroundOf(cmsData, page));
}

/** Each menu state's text, and the separator, with the state's own fill. */
const MENU_PAIRS: Record<string, string> = {
  color: "backgroundColor",
  separatorColor: "backgroundColor",
  hoverColor: "hoverBackgroundColor",
  activeColor: "activeBackgroundColor",
  expandedColor: "expandedBackgroundColor",
};
const MENU_FILLS = [
  "backgroundColor",
  "hoverBackgroundColor",
  "activeBackgroundColor",
  "expandedBackgroundColor",
];
/** The menu roles drawn on the header's bar; the others lie on the menu panel and its dropdowns. */
const BAR_ROLES = new Set(["bar", "button"]);
/** The roles NavigationFormatting also draws inside a dropdown or a mega menu, over that surface role's style. */
const SURFACE_ROLES = new Set(["child", "grandchild"]);
const MENU_SURFACES = ["dropdown", "mega"] as const;
const MENU_DEVICES = ["desktop", "mobile"];
const NO_SURFACES: HeaderSurfaces = { bar: false, panel: false };

/** Where the menu draws a style: the device, the fills merged there and whether the bar or panel under it changes. */
interface MenuPlace {
  device: string;
  fills: Record<string, unknown>;
  surface: boolean;
}

/**
 * The website's menu formatting (`menuTypography`) and a menu's own (`menuFormatting[id]`) with their own colours'
 * dark versions on the look system: each fill takes its version, and a text or separator colour where the fill under
 * it changes in dark everywhere the menu draws it: its state's fill, else the base `backgroundColor`, each as
 * resolveMenuStyle merges every layer for that role, surface, item and device (the website's under the menu's,
 * `common` under the roles, a role's parents under it, a dropdown's or mega menu's role over the child items in it, an
 * item's own style last, a breakpoint over the base), else the surface the role lies on (`surfaces`: the bar for `bar`
 * and `button`, the panel for the others). A style drawn for several roles or items, such as `common` or a parent
 * role, routes only where all of them change. Only a colour safeMenuStyle draws routes or counts as a fill, and only
 * as a form it admits, so a colour it drops stays dropped.
 */
export function menuFormattingsInDark<W, M>(
  theme: unknown,
  website: W,
  menu: M,
  surfaces: HeaderSurfaces = NO_SURFACES,
): { website: W; menu: M } {
  if (!isLookSystem(theme)) return { website, menu };
  const layers = [record(website), record(menu)];
  const items = record(layers[1].items);
  const fillsOf = (node: unknown) =>
    Object.fromEntries(
      MENU_FILLS.flatMap((key) => {
        const value = record(node)[key];
        return menuColour(value, true) ? [[key, value]] : [];
      }),
    );
  const placesOf = new Map<unknown, MenuPlace[]>();
  for (const role of menuRoles)
    for (const surface of SURFACE_ROLES.has(role)
      ? [undefined, ...MENU_SURFACES]
      : [undefined])
      for (const item of [undefined, ...Object.keys(items)]) {
        const styles = [
          ...layers.flatMap((layer) => [
            layer.common,
            ...roleChain(role).map((key) => record(layer.roles)[key]),
            surface && record(layer.roles)[surface],
          ]),
          item && items[item],
        ].filter(isRecord);
        for (const device of MENU_DEVICES) {
          const place: MenuPlace = {
            device,
            fills: Object.assign(
              {},
              ...styles.map((style) => ({
                ...fillsOf(record(style).base),
                ...fillsOf(record(style)[device]),
              })),
            ),
            surface: BAR_ROLES.has(role) ? surfaces.bar : surfaces.panel,
          };
          for (const style of new Set(styles)) {
            const places = placesOf.get(style);
            if (places) places.push(place);
            else placesOf.set(style, [place]);
          }
        }
      }
  const changes = (fill: unknown) =>
    menuColour(fill, true) ? fillChangesInDark(theme, fill) : undefined;
  const style = (node: Record<string, unknown>, places: MenuPlace[]) => {
    let result: Record<string, unknown> | undefined;
    for (const [key, value] of Object.entries(node)) {
      if (typeof value !== "string" || !menuColour(value)) continue;
      const fill = MENU_PAIRS[key];
      const routes = /backgroundcolor$/i.test(key)
        ? true
        : fill !== undefined &&
          places.length > 0 &&
          places.every(
            (place) =>
              changes(place.fills[fill]) ??
              changes(place.fills.backgroundColor) ??
              place.surface,
          );
      if (!routes) continue;
      const painted = ownColourCss(theme, value);
      if (painted !== value) (result ??= { ...node })[key] = painted;
    }
    return result ?? node;
  };
  const responsive = (value: unknown) => {
    if (!isRecord(value)) return value;
    const styles = value as Record<string, unknown>;
    const places = placesOf.get(value) ?? [];
    let result: Record<string, unknown> | undefined;
    for (const [key, node] of Object.entries(styles)) {
      if (!isRecord(node)) continue;
      const inner = style(
        node as Record<string, unknown>,
        places.filter((place) => key === "base" || place.device === key),
      );
      if (inner !== node) (result ??= { ...styles })[key] = inner;
    }
    return result ?? value;
  };
  const group = (value: unknown) => {
    if (!isRecord(value)) return value;
    const entries = value as Record<string, unknown>;
    let result: Record<string, unknown> | undefined;
    for (const [key, styles] of Object.entries(entries)) {
      const inner = responsive(styles);
      if (inner !== styles) (result ??= { ...entries })[key] = inner;
    }
    return result ?? value;
  };
  const layer = <T>(formatting: T): T => {
    if (!isRecord(formatting)) return formatting;
    const source = formatting as Record<string, unknown>;
    const routed: Record<string, unknown> = {
      ...source,
      common: responsive(source.common),
      roles: group(source.roles),
      items: group(source.items),
    };
    return (
      ["common", "roles", "items"].every((key) => routed[key] === source[key])
        ? formatting
        : Object.fromEntries(
            Object.entries(routed).filter(
              ([key, value]) => value !== undefined || key in source,
            ),
          )
    ) as T;
  };
  return { website: layer(website), menu: layer(menu) };
}
